import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db as firestoreDb } from '../firebaseConfig';
import { Group, Match } from '../types';

export const useMatchmakingBadges = (currentGroup: Group | null, userId: string | undefined) => {
    const [actionableMatchesCount, setActionableMatchesCount] = useState(0);

    const requestsCount = useMemo(() => {
        if (!currentGroup || !userId) return 0;
        let count = 0;
        
        const requests = currentGroup.matchmakingRequests || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Filter out past requests (archives) so they don't trigger phantom badges
        const activeRequests = requests.filter((r: any) => new Date(r.date) >= today);
        
        // 1. Host has candidates waiting
        count += activeRequests.filter((r: any) => r.hostId === userId && r.candidates && r.candidates.length > 0).length;
        
        // 2. User is invited to a match
        count += activeRequests.filter((r: any) => r.invitedPlayers && r.invitedPlayers.includes(userId)).length;
        
        return count;
    }, [currentGroup, userId]);

    // Fetch actionable matches (pending validation, contested)
    useEffect(() => {
        if (!currentGroup || !userId) {
            setActionableMatchesCount(0);
            return;
        }

        const q = query(
            collection(firestoreDb, 'groups', currentGroup.id, 'matches'),
            where('type', '==', 'matchmaking'),
            where('status', 'in', ['pending_validation', 'conflict', 'upcoming'])
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let count = 0;
            snapshot.forEach(doc => {
                const match = doc.data() as Match;
                const isParticipant = [match.p1Id, match.p2Id, match.p3Id, match.p4Id].includes(userId);
                
                if (isParticipant) {
                    if (match.status === 'conflict' || match.validationStatus === 'conflict') {
                        count++;
                    } else if ((match.status === 'pending_validation' || match.validationStatus === 'pending') && match.scoreProposal?.proposedBy !== userId) {
                        count++;
                    } else if (match.status === 'upcoming') {
                        // User mentioned they want to know if a new match they are in is created.
                        // Since we don't track "seen" state locally outside inbox, 
                        // we count all upcoming matches as a subtle reminder they have a game scheduled.
                        count++;
                    }
                }
            });
            setActionableMatchesCount(count);
        });

        return () => unsubscribe();
    }, [currentGroup, userId]);

    return {
        totalBadges: requestsCount + actionableMatchesCount,
        requestsCount,
        actionableMatchesCount
    };
};
